import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3f710bf6-804a-471c-9c53-69d13d905ea2/artifacts/bkfmz2dq_ChatGPT%20Image%20Feb%208%2C%202026%2C%2005_38_07%20PM.png";

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Our Services', path: '/services' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Get Quote', path: '/quote' },
  { name: 'Contact Us', path: '/contact' },
];

const services = [
  { name: 'Couch Manufacturing', path: '/services#manufacturing' },
  { name: 'Couch Sales', path: '/services#sales' },
  { name: 'Reupholstery', path: '/services#reupholstery' },
  { name: 'Custom Design', path: '/quote' },
];

const socialLinks = [
  { icon: 'fa-facebook-f', url: 'https://facebook.com', label: 'Facebook' },
  { icon: 'fa-instagram', url: 'https://instagram.com', label: 'Instagram' },
  { icon: 'fa-twitter', url: 'https://twitter.com', label: 'Twitter' },
  { icon: 'fa-tiktok', url: 'https://tiktok.com', label: 'TikTok' },
];

export const Footer = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
    }
  };

  return (
    <footer data-testid="main-footer" className="bg-leather text-white">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <img 
              src={LOGO_URL} 
              alt="Last Upholstery" 
              className="h-16 w-auto object-contain brightness-0 invert"
            />
            <p className="text-white/70 font-body text-sm leading-relaxed">
              Premium couch manufacturing, sales, and reupholstery services. 
              Crafting comfort for South African homes since 2015.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.icon}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-all"
                >
                  <i className={`fab ${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-gold">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white/70 hover:text-gold transition-colors font-body text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-gold">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    className="text-white/70 hover:text-gold transition-colors font-body text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-gold">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <i className="fas fa-phone text-gold mt-1"></i>
                <div>
                  <a href="tel:+27752781104" className="text-white/70 hover:text-gold block font-body text-sm">
                    +27 75 278 1104
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="fab fa-whatsapp text-gold mt-1"></i>
                <a 
                  href="https://wa.me/27752781104" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-gold font-body text-sm"
                >
                  WhatsApp Us
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="fas fa-envelope text-gold mt-1"></i>
                <a href="mailto:info@lastupholstery.co.za" className="text-white/70 hover:text-gold font-body text-sm">
                  info@lastupholstery.co.za
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="fas fa-map-marker-alt text-gold mt-1"></i>
                <span className="text-white/70 font-body text-sm">
                  Gauteng, South Africa
                </span>
              </li>
            </ul>

            {/* Install App Button */}
            {isInstallable && (
              <Button
                onClick={handleInstall}
                data-testid="footer-install-app-btn"
                className="mt-6 w-full bg-gold hover:bg-gold/90 text-white rounded-full"
              >
                <i className="fas fa-download mr-2"></i>
                Install App
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
            <p className="font-body">
              © {new Date().getFullYear()} Last Upholstery. All rights reserved.
            </p>
            <p className="font-body">
              Website designed by{' '}
              <a 
                href="https://www.webdevpro.co.za" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold hover:underline"
              >
                www.webdevpro.co.za
              </a>
              {' '}| Call/App{' '}
              <a href="tel:+27812159792" className="text-gold hover:underline">
                +27 812 159 792
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
